import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Chip,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import ManualClientTimeService from "../../../Services/manualClientTime.service";
import AuthService from "../../../Services/auth.service";

const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const emptyForm = {
    userId: "",
    userType: 2,
    caseId: "",
    hours: 0,
    minutes: 0,
    notes: "",
};

/**
 * Outside-Xtram manual time for a single attendance day.
 */
export default function ManualClientTimeSection({
    authToken,
    year,
    month,
    day,
    defaultUserId,
    defaultUserType,
    defaultUserName,
    userOptions = [],
    caseOptions = [],
    direction = "rtl",
}) {
    const { t } = useTranslation();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [accountantId, setAccountantId] = useState(null);

    const resolveDefaultUserType = useCallback(() => {
        if (defaultUserType === "SubAccountant" || defaultUserType === 8 || defaultUserType === "8") return 8;
        if (defaultUserType === "Accountant" || defaultUserType === 2 || defaultUserType === "2") return 2;
        return 2;
    }, [defaultUserType]);

    const clientOptions = useMemo(
        () => (caseOptions || []).filter((c) => c.value),
        [caseOptions]
    );

    const userSelectOptions = useMemo(() => {
        const mapped = (userOptions || []).map((u) => {
            const id = String(u.subAccountantId || u.accountantId || u.userId || u.value || "");
            const name = u.name || u.label || id;
            // Combined list stores accountant with subAccountantId === accountantId
            const isAccountant =
                Boolean(u.accountantId) &&
                (String(u.subAccountantId) === String(u.accountantId) || !u.subAccountantId);
            const mappedUserType = u.userType != null ? Number(u.userType) : isAccountant ? 2 : 8;
            return { value: id, label: name, userType: mappedUserType };
        }).filter((u) => u.value);

        // Ensure the opened schedule user is always available & selectable
        if (defaultUserId) {
            const defaultId = String(defaultUserId);
            const exists = mapped.some((u) => u.value === defaultId);
            if (!exists) {
                mapped.unshift({
                    value: defaultId,
                    label: defaultUserName || defaultId,
                    userType: resolveDefaultUserType(),
                });
            } else if (defaultUserName) {
                const idx = mapped.findIndex((u) => u.value === defaultId);
                if (idx >= 0 && (!mapped[idx].label || mapped[idx].label === defaultId)) {
                    mapped[idx] = { ...mapped[idx], label: defaultUserName };
                }
            }
        }

        return mapped;
    }, [userOptions, defaultUserId, defaultUserName, resolveDefaultUserType]);

    const resolveClientLabel = useCallback(
        (caseId) => clientOptions.find((c) => c.value === caseId)?.label || caseId,
        [clientOptions]
    );

    const lockedUserId = defaultUserId ? String(defaultUserId) : "";
    const lockedUserType = resolveDefaultUserType();
    const lockedUserLabel =
        defaultUserName ||
        userSelectOptions.find((u) => u.value === lockedUserId)?.label ||
        lockedUserId;

    const loadEntries = useCallback(async () => {
        if (!authToken || !year || !month || !day) return;
        setLoading(true);
        try {
            const accId = accountantId || (await AuthService.getCurrentUserTypeAccountantId());
            if (!accountantId && accId) setAccountantId(accId);
            if (!accId) return;

            const response = await ManualClientTimeService.Filter(
                {
                    accountantId: accId,
                    userIds: defaultUserId ? [String(defaultUserId)] : undefined,
                    year,
                    month,
                    day,
                },
                authToken
            );
            if (response.ok) {
                const result = await response.json();
                setEntries(result.data || result || []);
            } else {
                console.error("Failed to load manual client times");
            }
        } catch (err) {
            console.error("Error loading manual client times:", err);
        } finally {
            setLoading(false);
        }
    }, [authToken, year, month, day, accountantId, defaultUserId]);

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    const openCreate = () => {
        setEditingId(null);
        setForm({
            ...emptyForm,
            userId: lockedUserId,
            userType: lockedUserType,
        });
        setDialogOpen(true);
    };

    const openEdit = (entry) => {
        setEditingId(entry.id);
        setForm({
            userId: lockedUserId || String(entry.userId || ""),
            userType: lockedUserType || entry.userType,
            caseId: entry.caseId,
            hours: Math.floor((entry.durationMinutes || 0) / 60),
            minutes: (entry.durationMinutes || 0) % 60,
            notes: entry.notes || "",
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const durationMinutes = (Number(form.hours) || 0) * 60 + (Number(form.minutes) || 0);
        if (!form.caseId) {
            Swal.fire({
                icon: "warning",
                title: t("ClientRequired") || "Client is required",
            });
            return;
        }
        if (!(lockedUserId || form.userId)) {
            Swal.fire({
                icon: "warning",
                title: t("UserRequired") || "User is required",
            });
            return;
        }
        if (durationMinutes <= 0) {
            Swal.fire({
                icon: "warning",
                title: t("DurationRequired") || "Duration is required",
            });
            return;
        }

        setSaving(true);
        try {
            const accId = accountantId || (await AuthService.getCurrentUserTypeAccountantId());
            if (!accId) throw new Error("Missing accountant id");

            let response;
            if (editingId) {
                response = await ManualClientTimeService.Update(
                    editingId,
                    {
                        userId: lockedUserId || form.userId,
                        userType: lockedUserType || form.userType,
                        caseId: form.caseId,
                        year,
                        month,
                        day,
                        durationMinutes,
                        notes: form.notes || null,
                    },
                    authToken
                );
            } else {
                response = await ManualClientTimeService.Create(
                    {
                        userId: lockedUserId || form.userId,
                        userType: lockedUserType || form.userType,
                        caseId: form.caseId,
                        accountantId: accId,
                        year,
                        month,
                        day,
                        durationMinutes,
                        notes: form.notes || null,
                    },
                    authToken
                );
            }

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || "Save failed");
            }

            setDialogOpen(false);
            await loadEntries();
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: t("Error") || "Error",
                text: err.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (entry) => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: t("AreYouSure") || "Are you sure?",
            text: t("DeleteManualTimeConfirm") || "This will delete the outside-Xtram time entry.",
            showCancelButton: true,
            confirmButtonText: t("Delete") || "Delete",
            cancelButtonText: t("Cancel") || "Cancel",
        });
        if (!confirm.isConfirmed) return;

        try {
            const response = await ManualClientTimeService.Delete(entry.id, authToken);
            if (!response.ok) throw new Error("Delete failed");
            await loadEntries();
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: t("Error") || "Error",
                text: err.message,
            });
        }
    };

    const selectedClient = clientOptions.find((c) => c.value === form.caseId) || null;

    return (
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed rgba(0,0,0,0.12)" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    flexDirection: direction === "rtl" ? "row-reverse" : "row",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {t("OutsideXtramTime") || "Outside Xtram time"}
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openCreate}
                    sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.5, px: 1 }}
                >
                    {t("AddOutsideXtramTime") || "Add outside time"}
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                    <CircularProgress size={20} />
                </Box>
            ) : entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 1 }}>
                    {t("NoOutsideXtramTime") || "No outside-Xtram time entries for this day"}
                </Typography>
            ) : (
                <Table
                    size="small"
                    dir={direction}
                    sx={{
                        "& th, & td": { py: 0.5, fontSize: "0.8rem", textAlign: "center" },
                        "& thead th": { backgroundColor: "#f8f9fb" },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("Client") || "Client"}</TableCell>
                            <TableCell>{t("Duration") || "Duration"}</TableCell>
                            <TableCell>{t("Notes") || "Notes"}</TableCell>
                            <TableCell>{t("Actions") || "Actions"}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map((entry) => (
                            <TableRow key={entry.id}>
                                <TableCell>{resolveClientLabel(entry.caseId)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={formatDuration(entry.durationMinutes)}
                                        color="secondary"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 180, wordBreak: "break-word" }}>
                                    {entry.notes || "-"}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                        <IconButton size="small" color="primary" onClick={() => openEdit(entry)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(entry)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth dir={direction}>
                <DialogTitle>
                    {editingId
                        ? t("EditOutsideXtramTime") || "Edit outside Xtram time"
                        : t("AddOutsideXtramTime") || "Add outside Xtram time"}
                </DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <TextField
                        label={t("Accountant") || "Accountant / Subaccountant"}
                        size="small"
                        fullWidth
                        value={lockedUserLabel}
                        InputProps={{ readOnly: true }}
                        disabled
                    />
                    <Autocomplete
                        options={clientOptions}
                        value={selectedClient}
                        onChange={(_, opt) => setForm((prev) => ({ ...prev, caseId: opt?.value || "" }))}
                        getOptionLabel={(opt) => opt?.label || ""}
                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                        renderInput={(params) => (
                            <TextField {...params} label={t("Client") || "Client"} size="small" required />
                        )}
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label={t("Hours") || "Hours"}
                            type="number"
                            size="small"
                            fullWidth
                            inputProps={{ min: 0 }}
                            value={form.hours}
                            onChange={(e) => setForm((prev) => ({ ...prev, hours: Math.max(0, Number(e.target.value) || 0) }))}
                        />
                        <TextField
                            label={t("Minutes") || "Minutes"}
                            type="number"
                            size="small"
                            fullWidth
                            inputProps={{ min: 0, max: 59 }}
                            value={form.minutes}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    minutes: Math.min(59, Math.max(0, Number(e.target.value) || 0)),
                                }))
                            }
                        />
                    </Box>
                    <TextField
                        label={t("Notes") || "Notes"}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={form.notes}
                        onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>
                        {t("Cancel") || "Cancel"}
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : t("Save") || "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
